import { FC, useCallback, useRef, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
} from '@chakra-ui/react';
import { MdCloudDownload, MdCode, MdDataObject } from 'react-icons/md';

import { Order, OrderDetail } from '@app/api/models/order';
import { RequestPayload } from '@app/api/types';
import { OrderListPayload } from '@app/api/orders';
import { Dialog } from '@app/components/common';
import { QueryTable } from '@app/components/common/table';
import { useQueryTable } from '@app/components/common/table/query-table';
import { useAlert, useApi, useFetchData } from '@app/hooks';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';

import { useColumns } from './columns';
import { downloadJson, downloadXml, ExportFormat } from './export-utils';
import { OrderForm } from './form';

const PAGE_SIZE = 100;

const buildListFilter = (
  raw: Record<string, string | string[] | number> = {}
): OrderListPayload['filter'] => {
  const filter: OrderListPayload['filter'] = {};
  if (raw.distributor_id) {filter.distributor_id = Number(raw.distributor_id);}
  if (raw.outlet_id) {filter.outlet_id = Number(raw.outlet_id);}
  if (raw.status) {filter.status = String(raw.status);}
  if (raw.username) {filter.username = String(raw.username);}
  if (raw.created_b) {filter.created_from = String(raw.created_b);}
  if (raw.created_e) {filter.created_to = String(raw.created_e);}
  return filter;
};

const OrderDialog: FC<{
  open: boolean;
  order?: Order;
  canChange: boolean;
  onClose: () => void;
}> = ({ open, order, canChange, onClose }) => {
  const { refetch } = useQueryTable();
  if (!order) {
    return null;
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Заказ #${order.pid}`}
      width={800}
      footerless
    >
      {open && (
        <OrderForm
          order={order}
          canChange={canChange}
          onSubmit={() => {
            refetch();
            onClose();
          }}
          onCancel={onClose}
        />
      )}
    </Dialog>
  );
};

export const OrdersTable: FC = () => {
  const api = useApi();
  const { error, success, info } = useAlert();
  const canChange = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });
  const [selected, setSelected] = useState<Order>();
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const lastFilterRef = useRef<OrderListPayload['filter']>({});
  const lastSortRef = useRef('+pid');

  const onOpen = useCallback((order: Order) => {
    setSelected(order);
    setOpen(true);
  }, []);

  const fetchOrderDetails = useCallback(async (orders: Order[]) => {
    const details: OrderDetail[] = [];
    for (const order of orders) {
      const response = await api.orders.getOrder(order.pid);
      if (response?.data) {
        details.push(response.data);
      }
    }
    return details;
  }, [api]);

  const fetchFilteredOrders = useCallback(async () => {
    const filter = lastFilterRef.current;
    const sort = lastSortRef.current || '-pid';
    const all: Order[] = [];
    let offset = 0;
    let total = Infinity;

    while (offset < total) {
      const response = await api.orders.getOrders({
        limit: PAGE_SIZE,
        offset,
        sort,
        filter,
      });
      const page = response?.data?.data || [];
      total = response?.data?.total ?? page.length;
      all.push(...page);
      if (!page.length) {
        break;
      }
      offset += PAGE_SIZE;
    }

    return all;
  }, [api]);

  const exportData = useCallback((
    data: unknown,
    format: ExportFormat,
    filename: string,
    rootName: string
  ) => {
    if (format === 'json') {
      downloadJson(data, filename);
    } else {
      downloadXml(data, filename, rootName);
    }
  }, []);

  const onExportOrder = useCallback(async (order: Order, format: ExportFormat) => {
    setExporting(true);
    try {
      const response = await api.orders.getOrder(order.pid);
      const detail = response?.data;
      if (!detail) {
        error('Не удалось загрузить заказ');
        return;
      }
      exportData(detail, format, `order-${order.pid}`, 'order');
      success(`Заказ #${order.pid} выгружен (${format.toUpperCase()})`);
    } catch (e) {
      error(e.message || 'Ошибка выгрузки заказа');
    } finally {
      setExporting(false);
    }
  }, [api, error, exportData, success]);

  const onExportFiltered = useCallback(async (format: ExportFormat) => {
    setExporting(true);
    try {
      info('Собираем отфильтрованные заказы…');
      const orders = await fetchFilteredOrders();
      if (!orders.length) {
        error('Нет заказов для выгрузки');
        return;
      }
      const details = await fetchOrderDetails(orders);
      if (format === 'json') {
        exportData(
          { total: details.length, orders: details },
          format,
          `orders-${new Date().toISOString().slice(0, 10)}`,
          'orders'
        );
      } else {
        exportData(
          details,
          format,
          `orders-${new Date().toISOString().slice(0, 10)}`,
          'orders'
        );
      }
      success(`Выгружено заказов: ${details.length} (${format.toUpperCase()})`);
    } catch (e) {
      error(e.message || 'Ошибка выгрузки заказов');
    } finally {
      setExporting(false);
    }
  }, [error, exportData, fetchFilteredOrders, fetchOrderDetails, info, success]);

  const columns = useColumns({ onOpen, onExport: onExportOrder, canChange });

  const fetchOrders = useCallback(
    (payload: RequestPayload) => {
      const raw = (payload.filter || {}) as Record<string, string | string[] | number>;
      const filter = buildListFilter(raw);
      lastFilterRef.current = filter;
      lastSortRef.current = payload.sort || '-pid';

      return api.orders.getOrders({
        limit: payload.limit,
        offset: payload.offset,
        sort: payload.sort,
        filter,
      });
    },
    [api]
  );

  const [fetchData] = useFetchData<Order>({ method: fetchOrders });

  return (
    <QueryTable<Order>
      title="Заказы"
      columns={columns}
      fetchData={fetchData}
      keyProperty="pid"
      initialSort={{ name: 'pid', direction: 'desc' }}
      additionalToolbar={
        <Menu>
          <Tooltip label="Выгрузить отфильтрованные">
            <MenuButton
              as={IconButton}
              aria-label="export-filtered"
              icon={<MdCloudDownload />}
              isDisabled={exporting}
            />
          </Tooltip>
          <MenuList>
            <MenuItem
              icon={<MdDataObject />}
              onClick={() => onExportFiltered('json')}
              isDisabled={exporting}
            >
              JSON (все отфильтрованные)
            </MenuItem>
            <MenuItem
              icon={<MdCode />}
              onClick={() => onExportFiltered('xml')}
              isDisabled={exporting}
            >
              XML (все отфильтрованные)
            </MenuItem>
          </MenuList>
        </Menu>
      }
    >
      <OrderDialog
        open={open}
        order={selected}
        canChange={canChange}
        onClose={() => setOpen(false)}
      />
    </QueryTable>
  );
};

export default OrdersTable;
